import React, { Component } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import PropTypes from 'prop-types';
import { List, Map, fromJS } from 'immutable';
import { Flex, Box } from 'grid-styled';

import connect from '../store/connect';
import { fetchArticlePageView, fetchCampaignProducts, setViewstate, removeViewstate } from '../actions/actionCreators';
import Page from './Page';
import InstagramSection from '../components/InstagramSection';
import ProductListWithFilters from '../components/ProductListWithFilters';

import { Container } from '../styles/Container';
import { Heading } from '../styles/Heading';
import { Preamble } from '../styles/Preamble';
import NotFoundPage from './NotFoundPage';
import ErrorPage from './ErrorPage';
import { NavigationButton } from '../components/NavigationButton';
import { fontFamilyBody } from '../styles/helpers/mixins';
import { Conditional } from '../components/Conditional';
import CampaignListItem from '../components/CampaignListItem';
import { SITE_ROOT_URL } from '../constants/endpoints';
import { Section } from '../styles/Section';

class CampaignListPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showAllCampaigns: true
    };

    this.toggleShowAllCampaigns = this.toggleShowAllCampaigns.bind(this);
  }

  componentDidMount() {
    const pageUrl = this.props.location.pathname;
    this.props.fetchArticlePageView({ urlPath: pageUrl, includeChildren: true });

    const productListId = this.props.currentPage.getIn(['fields', 'campaignProductListId', 'id']);
    if (productListId) {
      this.props.fetchCampaignProducts({
        id: productListId,
        url: pageUrl
      });
    }

    this.setDefaultFilters(pageUrl);
  }

  componentDidUpdate() {
    const pageUrl = this.props.location.pathname;
    const productListId = this.props.currentPage.getIn(['fields', 'campaignProductListId', 'id']);

    if (this.props.productsRequest.isEmpty() && productListId) {
      this.props.fetchCampaignProducts({
        id: productListId,
        url: pageUrl
      });

      this.setDefaultFilters(pageUrl);
    }
  }

  setDefaultFilters(categoryId) {
    const { currentCategory } = this.props;
    if (currentCategory !== categoryId) {
      this.props.setViewstate('currentCategory', categoryId);
      this.props.setViewstate('sortAscending', false);
      this.props.setViewstate('sortBy', 'name');
      this.props.removeViewstate('filter');
      this.props.setViewstate('pagination', 0);
    }
  }

  toggleShowAllCampaigns() {
    this.setState(state => ({ showAllCampaigns: !state.showAllCampaigns }));
  }

  renderCampaignItems() {
    return this.props.currentPage
      .get('children', List())
      .map(child => (
        <CampaignListItem
          to={child.get('url')}
          key={child.get('id')}
          title={child.getIn(['fields', 'title'])}
          preamble={child.getIn(['fields', 'introduction'])}
          linkTitle={`Se ${child.getIn(['fields', 'title'])}`}
          image={SITE_ROOT_URL + child.getIn(['fields', 'listImage', 'url'])}
        />
      ))
      .toArray();
  }

  render() {
    const { currentPage, request, campaign, products, productsRequest } = this.props;
    const { showAllCampaigns } = this.state;

    if (request.get('statusCode') === 404) return <NotFoundPage />;
    if (request.get('status') === 'ERROR') return <ErrorPage />;

    const campaigns = this.renderCampaignItems();

    const productListId = this.props.currentPage.getIn(['fields', 'campaignProductListId', 'id']);
    const campaignProductIds = fromJS(campaign.get(productListId) || []);
    const campaignProducts = campaignProductIds.map(id => products.get(id));

    return (
      <Page meta={this.props.currentPage.get('metaData')} bgColor="#f2f2f2">
        <Container>
          <Heading big center>
            {currentPage.getIn(['fields', 'title'])}
          </Heading>
          <Preamble>{currentPage.getIn(['fields', 'introduction'])}</Preamble>

          <Conditional show={!showAllCampaigns}>
            <ThemeProvider
              theme={{
                breakpoints: ['1100px']
              }}
            >
              <Flex flexWrap="wrap" mx={[0, -15]} mt={[30, 50]}>
                {this.props.currentPage.get('children') &&
                  this.props.currentPage
                    .get('children')
                    .take(3)
                    .map(item => (
                      <Box key={item.get('url')} width={[1, 1 / 3]} px={[0, 15]} py={['5px', 0]}>
                        <NavigationButton
                          url={item.get('url')}
                          title={item.getIn(['fields', 'title'])}
                          image={item.getIn(['fields', 'listImage', 'url'])}
                        />
                      </Box>
                    ))}
              </Flex>
            </ThemeProvider>
            <SeAllCampaignsButton onClick={this.toggleShowAllCampaigns}>Se våra kampanjer</SeAllCampaignsButton>
          </Conditional>

          <Conditional show={showAllCampaigns}>
            <SeAllCampaignsButton onClick={this.toggleShowAllCampaigns}>Se alla kampanjprodukter</SeAllCampaignsButton>
          </Conditional>
        </Container>

        <Conditional show={!showAllCampaigns}>
          <ProductListWithFilters includeCategoryFilter request={productsRequest} allProducts={campaignProducts} />
        </Conditional>

        <Conditional show={showAllCampaigns}>
          <Section pb={80}>
            <Container>{campaigns}</Container>
          </Section>
        </Conditional>

        <InstagramSection />
      </Page>
    );
  }
}

CampaignListPage.defaultProps = {
  currentPage: Map(),
  request: Map(),
  productsRequest: Map(),
  campaign: Map(),
  products: Map(),
  currentCategory: undefined
};

CampaignListPage.propTypes = {
  fetchArticlePageView: PropTypes.func.isRequired,
  fetchCampaignProducts: PropTypes.func.isRequired,
  setViewstate: PropTypes.func.isRequired,
  removeViewstate: PropTypes.func.isRequired,
  currentPage: PropTypes.instanceOf(Map),
  request: PropTypes.instanceOf(Map),
  location: PropTypes.object.isRequired,
  productsRequest: PropTypes.instanceOf(Map),
  campaign: PropTypes.instanceOf(Map),
  products: PropTypes.instanceOf(Map),
  currentCategory: PropTypes.string
};

export default connect(
  CampaignListPage,
  { fetchArticlePageView, fetchCampaignProducts, setViewstate, removeViewstate },
  (store, props) => ({
    currentPage: store.page.data.get(props.location.pathname),
    request: store.page.request.get(props.location.pathname),
    campaign: store.campaign,
    products: store.products.data,
    productsRequest: store.products.request.get(props.location.pathname),
    currentCategory: store.viewstate.get('currentCategory')
  })
);

const SeAllCampaignsButton = styled.button`
  ${fontFamilyBody};
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 3px;
  text-align: center;
  color: #000000;
  text-transform: uppercase;
  padding: 8px;
  margin: 20px auto 4px;
  display: block;
  background-color: transparent;
  border: none;
  cursor: pointer;
`;



// WEBPACK FOOTER //
// ./src/pages/CampaignListPage.js